import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useStableAuth } from "@/hooks/useStableAuth";
import { useProductUnits } from "@/hooks/useProductUnits";

// I.151 1b3-b: cadastro de unidade alternativa do produto sem sair do assistente de nota
// (ex.: "1 CX = 18 Un"). Grava só por upsert_product_unit; o banco converte nas compras.

interface QuickProductUnitDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  productId: string;
  defaultMeasurementUnitId?: string | null;
  defaultFactor?: number | null;
  onCreated: (productUnitId: string) => void;
}

export function QuickProductUnitDialog({
  open,
  onOpenChange,
  productId,
  defaultMeasurementUnitId,
  defaultFactor,
  onCreated,
}: QuickProductUnitDialogProps) {
  const { companyId } = useStableAuth();
  const { base, upsertUnit, isSaving } = useProductUnits(productId);
  const [unitId, setUnitId] = useState("");
  const [factor, setFactor] = useState("");

  const { data: vocab = [] } = useQuery({
    queryKey: ["measurement-units-active", companyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("measurement_units")
        .select("id, name, sefaz_code")
        .eq("company_id", companyId as string)
        .eq("active", true)
        .order("name");
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!companyId && open,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (!open) return;
    setUnitId(defaultMeasurementUnitId && defaultMeasurementUnitId !== base?.id ? defaultMeasurementUnitId : "");
    setFactor(defaultFactor && defaultFactor > 0 ? String(defaultFactor) : "");
  }, [open, defaultMeasurementUnitId, defaultFactor, base?.id]);

  const factorNum = parseFloat(factor.replace(",", "."));
  const chosen = vocab.find((u) => u.id === unitId);
  const canSave = !!unitId && Number.isFinite(factorNum) && factorNum > 0 && !isSaving && !!base;

  const handleSave = async () => {
    if (!canSave) return;
    try {
      const id = await upsertUnit({ productId, measurementUnitId: unitId, factorToBase: factorNum, allowPurchase: true });
      onCreated(id);
      onOpenChange(false);
    } catch {
      // toast no onError da mutation
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Cadastrar unidade do produto</DialogTitle>
        </DialogHeader>
        {!base ? (
          <p className="text-sm text-muted-foreground">
            O produto não tem unidade base definida. Defina a unidade na ficha do produto antes.
          </p>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Unidade</Label>
              <Select value={unitId} onValueChange={setUnitId}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  {vocab
                    .filter((u) => u.id !== base.id)
                    .map((u) => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.name} {u.sefaz_code ? `(${u.sefaz_code})` : ""}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>
                Quantos {base.sigla} em 1 {chosen?.sefaz_code || chosen?.name || "unidade"}
              </Label>
              <Input type="number" min="0" step="any" value={factor} onChange={(e) => setFactor(e.target.value)} />
            </div>
            {canSave && (
              <p className="text-sm text-muted-foreground">
                1 {chosen?.sefaz_code || chosen?.name} = {factorNum.toLocaleString("pt-BR", { maximumFractionDigits: 6 })}{" "}
                {base.sigla}
              </p>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={!canSave}>
            {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Salvar unidade
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
